import imageTopology from 'assets/labyrinth/labyrinth-topology.png';
import imageTopologyLarge from 'assets/labyrinth/labyrinth-topology-large.png';
import imageTopologyPlaceholder from 'assets/labyrinth/labyrinth-topology-placeholder.png';
import { Button } from 'components/Button';
import { CodeBlock } from 'components/CodeBlock';
import { Footer } from 'components/Footer';
import { Image } from 'components/Image';
import { Meta } from 'components/Meta';
import { SpecTable } from 'components/SpecTable';
import { useTheme } from 'components/ThemeProvider';
import { media } from 'utils/style';
import {
  ProjectContainer,
  ProjectHeader,
  ProjectSection,
  ProjectSectionColumns,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'layouts/Project';
import { Fragment } from 'react';
import styles from './labyrinth.module.css';

const title = 'Labyrinth';
const description =
  'lab.yml describes a two-node Proxmox cluster. One command builds its containers, ' +
  'routing, SSO, firewall rules and dashboards.';
const roles = ['OpenTofu + Ansible', 'Infrastructure as Code', 'Solo Project'];

const applySession = `$ just apply
generate   tfvars · inventory · traefik · authentik · prometheus
tofu       containers, DNS records, firewall rules, tailnet enrolment
ansible    mounts, packages, services
lab converged`;

const labYml = `# lab.yml — the single source of truth
cluster:
  name: void

nodes:
  leviathan:          # HP DL380 G9 — bulk storage, most stacks
    datastore: local-lvm
  erebus:             # consumer build with a GPU — media and hot tier
    datastore: local-zfs

hosts:
  ingress:
    node: leviathan
    stack: ingress
    cores: 2
    memory: 2048
  jellyfin:
    node: erebus
    stack: jellyfin
    cores: 4
    memory: 8192
    gpu: true`;

const stacks = [
  {
    name: 'Ingress',
    body: 'Traefik sits behind CrowdSec. Every cross-host route terminates at Traefik and is generated from the hostname declared by its stack.',
  },
  {
    name: 'Identity',
    body: 'Authentik provides single sign-on through forward auth on the routes. Stack metadata generates its providers and applications.',
  },
  {
    name: 'Monitoring',
    body: 'Prometheus, Grafana and Loki use scrape targets from the generated host inventory. New containers are monitored as soon as they exist.',
  },
  {
    name: 'VPN gateway',
    body: 'Downloaders can reach the internet only through the VPN gateway. If the tunnel goes down, their traffic stops.',
  },
  {
    name: 'Media',
    body: 'Jellyfin is installed natively to use the GPU for transcoding. Everyone else in the house makes requests through Jellyseerr.',
  },
  {
    name: 'Fleet tooling',
    body: 'Portainer shows logs and stats. apt-cacher-ng caches package updates for thirteen containers, and every build regenerates the docs site from lab.yml.',
  },
];

export function Labyrinth() {
  const { themeId } = useTheme();
  const isDark = themeId === 'dark';

  return (
    <Fragment>
      <Meta title={title} prefix="Projects" description={description} />
      <ProjectContainer>
        <ProjectHeader title={title} description={description} roles={roles} />
        <ProjectSection padding="top">
          <ProjectSectionContent>
            <CodeBlock className={styles.hero} variant="terminal" title="just apply">
              {applySession}
            </CodeBlock>
            <ProjectTextRow>
              <ProjectSectionHeading>Machines rebuilt from version control</ProjectSectionHeading>
              <ProjectSectionText>
                I used to configure containers by hand. Deploying meant remembering which
                machine had spare cores, which reverse-proxy config needed a new hostname,
                and which firewall rule I had opened and forgotten. Labyrinth describes
                the machines in version control and rebuilds them without patching by
                hand. It has roughly 7,300 lines of OpenTofu, Ansible and Python.
                CI validates every change to the
                description before it reaches the hardware.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection light={isDark}>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>How it works</ProjectSectionHeading>
              <ProjectSectionText>
                There are three things I edit: lab.yml, which says where things run; a
                directory per stack, which says what a thing is; and an encrypted vault
                for secrets. Everything downstream is generated: Terraform variables, the
                Ansible inventory, Traefik routes, Authentik blueprints, DNS records,
                firewall rules and Prometheus scrape targets. A hand edit to any of those
                means the generator has a bug.
              </ProjectSectionText>
              <ProjectSectionText>
                OpenTofu makes containers exist and owns the state that lives off the
                cluster; Ansible takes them from empty container to running service. The
                split is deliberate, and it is the reason a node can be wiped and come
                back the same.
              </ProjectSectionText>
              <CodeBlock className={styles.code} lang="yaml" lineNumbers>
                {labYml}
              </CodeBlock>
              <ProjectSectionText>
                The build generates the topology view from lab.yml, so it stays in step
                with what is deployed.
              </ProjectSectionText>
              <Image
                raised
                srcSet={[imageTopology, imageTopologyLarge]}
                placeholder={imageTopologyPlaceholder}
                alt="The generated topology view: a service map showing how stacks relate, with routes through ingress, SSO via authentik, VPN egress and cross-stack dependencies, each host panel listing its stack and routes."
                sizes={`(max-width: ${media.mobile}px) 100vw, 80vw`}
              />
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
          <ProjectSection>
            <ProjectSectionColumns>
              <ProjectSectionContent>
                <ProjectTextRow>
                  <ProjectSectionHeading>
                    Six groups of services
                  </ProjectSectionHeading>
                  <ProjectSectionText>
                    Each stack has one directory, one service and one container. Its
                    metadata defines routing, SSO, firewall openings and storage mounts.
                    Adding a service means adding a folder instead of editing six files.
                    Thirteen stacks are running today in six groups.
                  </ProjectSectionText>
                </ProjectTextRow>
              </ProjectSectionContent>
              <ProjectSectionContent>
                <ul className={styles.stacks}>
                  {stacks.map(({ name, body }) => (
                    <li className={styles.stack} key={name}>
                      <span className={styles.stackName}>{name}</span>
                      <ProjectSectionText size="s">{body}</ProjectSectionText>
                    </li>
                  ))}
                </ul>
              </ProjectSectionContent>
            </ProjectSectionColumns>
          </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>What it runs on</ProjectSectionHeading>
              <ProjectSectionText>
                The cluster has two very different machines: a decade-old rack server
                with a lot of disks and a consumer desktop with a GPU. lab.yml assigns
                each service to a node, making that mismatch a scheduling detail I do
                not have to remember.
              </ProjectSectionText>
            </ProjectTextRow>
            <SpecTable
              heading="Hardware"
              rows={[
                { label: 'Cluster', value: 'Two-node Proxmox VE cluster, LXC-first' },
                {
                  label: 'Nodes',
                  value: [
                    'HP DL380 G9: bulk storage and most stacks',
                    'Consumer build with an RTX 2070: media and the hot tier',
                  ],
                },
                {
                  label: 'Storage',
                  value: 'ZFS: raidz2 across the server’s SAS disks for bulk, NVMe pools for hot data',
                },
                {
                  label: 'Network',
                  value: [
                    'UniFi Dream Machine, VLAN-segmented',
                    'Tailscale for remote access, with tag-based ACLs',
                  ],
                },
                {
                  label: 'Toolchain',
                  value: 'OpenTofu, Ansible, Python generators, CI on every change',
                },
              ]}
            />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow center centerMobile noMargin>
              <ProjectSectionHeading>Worth stealing the pattern</ProjectSectionHeading>
              <ProjectSectionText>
                The repository is private. lab.yml describes the specific hardware in my
                house, so it would need changes to deploy elsewhere. The overall structure
                applies elsewhere, and I am happy to walk through the generator, the stack
                contract and the parts that resisted being declarative.
              </ProjectSectionText>
              <Button secondary iconHoverShift icon="chevronRight" href="/contact">
                Ask me about it
              </Button>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}
